import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Alert, Anchor, Button, Card, message, Space, Spin, Tabs, Typography } from 'antd'
import axios from 'axios'
import CnGeneralConfiguration, { CnGeneralConfigurationHandle } from './components/CnGeneralConfiguration'
import { CnColumns, CnColumnsHandle } from './components/CnColumns'
import CnAgencies, { CnAgenciesHandle } from './components/agencies/CnAgencies'
import CnStatus, { CnStatusHandle } from './components/CnStatus'
import { Status } from './components/CnStatus'
import CnCollaboratorAccess, {
  CollaboratorRef,
  CnCollaboratorAccessHandle,
  StoredCollaboratorAccess,
} from './components/CnCollaboratorAccess'
import { saveCnSoftAccess } from './saveCnSoftAccess'
import { ContactInfo } from './components/CnGeneralConfiguration'
import CnDatahub, { CnDatahubHandle } from './components/datahub/CnDatahub'
import { DatahubSaveData } from './components/datahub/datahub-interfaces'
import { CnOrgaDataPage, CnOrgaDataTab } from './CnOrgaDataPage'
import { fetchMsEnseignesGroupsData, MsEnseignesGroup } from './components/ms/msEnseignesGroups-service'

export interface CnSoftAccessInitData {
  softaccessId?: string
  fileId?: string
  agencies: any[]
  rg2: any[]
  status: Status[] | null
  msPro: boolean
  name: string
  rg3DefaultTab: string
  customContact: ContactInfo | null
  adminAccess: string
  collaborators: CollaboratorRef[]
  accessCollaborator: StoredCollaboratorAccess[] | null
  orgaId: string
  isNew: boolean
  cancelUrl: string
  datahub?: DatahubSaveData | null
}

interface CnSoftAccessPageProps {
  orgaId: string
  softaccessId: string
}

type MainTab = 'configuration' | 'donnees'

const SECTIONS = [
  { id: 'general', title: 'Configuration générale' },
  { id: 'collaborators', title: 'Accès collaborateurs' },
  { id: 'status', title: 'Statut Client / Prospect' },
  { id: 'agencies', title: 'Agences' },
  { id: 'columns', title: 'Gestion des colonnes' },
  { id: 'datahub', title: 'Datahub' },
] as const

function scrollToSection(sectionId: string) {
  document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'end' })
}

function resolveMainTab(search: string): MainTab {
  const tab = new URLSearchParams(search).get('tab')
  return tab === 'donnees' ? 'donnees' : 'configuration'
}

function resolveDonneesSubTab(search: string): CnOrgaDataTab | undefined {
  const sub = new URLSearchParams(search).get('sub')
  if (sub === 'caFile' || sub === 'collsAgenciesOrRg2' || sub === 'ape') return sub
  return undefined
}

export function CnSoftAccessPage({ orgaId, softaccessId: routeSoftaccessId }: CnSoftAccessPageProps) {
  const history = useHistory()
  const location = useLocation()
  const [softaccessId, setSoftaccessId] = useState(routeSoftaccessId)
  const [initData, setInitData] = useState<CnSoftAccessInitData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msEnseignes, setMsEnseignes] = useState<string[]>([])
  const [msGroups, setMsGroups] = useState<MsEnseignesGroup[]>([])  
  const [mainTab, setMainTab] = useState<MainTab>(() => resolveMainTab(location.search))
  const generalConfigRef = useRef<CnGeneralConfigurationHandle>(null)
  const collaboratorAccessRef = useRef<CnCollaboratorAccessHandle>(null)
  const statusRef = useRef<CnStatusHandle>(null)
  const agenciesRef = useRef<CnAgenciesHandle>(null)
  const columnsRef = useRef<CnColumnsHandle>(null)
  const datahubRef = useRef<CnDatahubHandle>(null)

  useEffect(() => {
    setSoftaccessId(routeSoftaccessId)
  }, [routeSoftaccessId])

  useEffect(() => {
    setMainTab(resolveMainTab(location.search))
  }, [location.search])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const { data } = await axios.get<CnSoftAccessInitData>(
          `/organisations/${orgaId}/cn/softwaresaccesses/${routeSoftaccessId}/init`,
        )
        setInitData(data)
      } catch (error) {
        console.error(error)
        message.error("Impossible de charger l'accès logiciel")
      } finally {
        setLoading(false)
      }
    }
    load()
    fetchMsEnseignesGroupsData()
    .then((data) => {
        setMsEnseignes(data.enseignes);
        setMsGroups(data.groups);
    })
    .catch(() => {
        setMsEnseignes([]);
        setMsGroups([]);
    });
  }, [orgaId, routeSoftaccessId])

  useEffect(() => {
    if (loading || !initData || mainTab !== 'configuration') return
    const hashId = window.location.hash.slice(1)
    if (hashId && SECTIONS.some((section) => section.id === hashId)) {
      requestAnimationFrame(() => scrollToSection(hashId))
    }
  }, [loading, initData, mainTab])

  const updateMainTabInUrl = useCallback(
    (tab: MainTab) => {
      const params = new URLSearchParams(location.search)
      if (tab === 'configuration') {
        params.delete('tab')
        params.delete('sub')
      } else {
        params.set('tab', 'donnees')
      }
      const search = params.toString()
      history.replace({
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: tab === 'configuration' ? location.hash : '',
      })
    },
    [history, location.pathname, location.search, location.hash],
  )

  const handleMainTabChange = useCallback(
    (key: string) => {
      const tab = key as MainTab
      setMainTab(tab)
      updateMainTabInUrl(tab)
    },
    [updateMainTabInUrl],
  )

  const handleSave = useCallback(async () => {
    if (agenciesRef.current?.hasFormulaErrors()) {
      setMainTab('configuration')
      updateMainTabInUrl('configuration')
      scrollToSection('agencies')
      message.error(
        'Certaines formules de gestion des agences présentent des erreurs. Veuillez les corriger avant de poursuivre.',
      )
      return
    }

    const datahubErrors = datahubRef.current?.getValidationErrors() ?? []
    if (datahubErrors.length > 0) {
      setMainTab('configuration')
      updateMainTabInUrl('configuration')
      scrollToSection('datahub')
      for (const err of datahubErrors) {
        message.error(err, 6)
      }
      return
    }

    const generalConfig = generalConfigRef.current?.getSaveData()
    const agenciesData = agenciesRef.current?.getSaveData()
    const cnColsData = columnsRef.current?.getCnColsData()
    const datahubData = datahubRef.current?.getSaveData()

    if (!generalConfig || !agenciesData || !cnColsData || !datahubData) {
      message.error("Impossible de récupérer les données du formulaire")
      return
    }

    const wasNew = softaccessId === 'new'
    setSaving(true)
    try {
      await saveCnSoftAccess(orgaId, softaccessId, {
        cn_cols_data: cnColsData,
        access_collaborator: collaboratorAccessRef.current?.getSaveData(),
        agencies: agenciesData.agencies,
        rg2: agenciesData.rg2,
        status: statusRef.current?.getSaveData(),
        administrator_access_collaborator: generalConfig.administrator_access_collaborator,
        ms_pro: generalConfig.ms_pro,
        name: generalConfig.name,
        deletedGroupementsIds: agenciesData.deletedGroupementsIds,
        rg3_default_tab: generalConfig.rg3_default_tab,
        custom_contact_support: generalConfig.custom_contact_support,
        datahub: JSON.stringify(datahubData),
      })
      message.success(
        wasNew
          ? 'Accès logiciel créé. Les agences sont traitées en arrière-plan.'
          : 'Accès logiciel enregistré. Les agences sont traitées en arrière-plan.',
      )
      history.push(`/organisations/${orgaId}/softwares`)
    } catch (error) {
      console.error(error)
      message.error("Erreur lors de l'enregistrement de l'accès logiciel")
    } finally {
      setSaving(false)
    }
  }, [history, orgaId, softaccessId, updateMainTabInUrl])

  const handleCancel = useCallback(() => {
    history.push(`/organisations/${orgaId}/softwares`)
  }, [history, orgaId])

  const isNew = softaccessId === 'new'
  const rg2Json = initData ? JSON.stringify(initData.rg2) : '[]'
  const donneesSubTab = resolveDonneesSubTab(location.search)

  if (loading || !initData) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  const configurationContent = (
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: '#fff',
          padding: '8px 0 12px',
          marginBottom: 24,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Anchor
          direction="horizontal"
          targetOffset={64}
          items={SECTIONS.map(({ id, title }) => ({
            key: id,
            href: `#${id}`,
            title,
          }))}
          style={{ flexWrap: 'wrap' }}
        />
      </div>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <SectionCard id="general" title="Configuration générale">
          <CnGeneralConfiguration
            ref={generalConfigRef}
            orgaId={orgaId}
            softaccessId={softaccessId}
            msPro={initData.msPro}
            name={initData.name}
            rg3DefaultTab={initData.rg3DefaultTab}
            customContact={initData.customContact}
            adminAccess={initData.adminAccess}
          />
        </SectionCard>

        <SectionCard id="collaborators" title="Accès collaborateurs">
          <CnCollaboratorAccess
            ref={collaboratorAccessRef}
            collaborators={initData.collaborators}
            initialAccess={initData.accessCollaborator}
          />
        </SectionCard>

        <SectionCard id="status" title="Statut Client / Prospect">
          <CnStatus ref={statusRef} initialStatus={initData.status} />
        </SectionCard>

        <SectionCard id="agencies" title="Agences">
          <CnAgencies
            ref={agenciesRef}
            orgaId={orgaId}
            softaccessId={softaccessId}
            initialAgencies={JSON.stringify(initData.agencies)}
            initialRg2={rg2Json}
            initialStatus={initData.status ? JSON.stringify(initData.status) : undefined}
            msEnseignes={msEnseignes}
            msGroups={msGroups}
          />
        </SectionCard>

        <SectionCard id="columns" title="Gestion des colonnes">
          <CnColumns
            ref={columnsRef}
            cnSoftAccessId={isNew ? undefined : softaccessId}
            orgaId={orgaId}
            initialAgencies={JSON.stringify(initData.agencies)}
            softaccessId={softaccessId}
            rg2Json={rg2Json}
            msEnseignes={msEnseignes}
            msGroups={msGroups}
          />
        </SectionCard>

        <SectionCard id="datahub" title="Datahub">
          <CnDatahub ref={datahubRef} orgaId={orgaId} initialData={initData.datahub} />
        </SectionCard>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleCancel}>Annuler</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Enregistrer
          </Button>
        </div>
      </Space>
    </>
  )

  const donneesContent = isNew ? (
    <Alert
      type="info"
      showIcon
      message="Enregistrez d'abord la configuration"
      description="L'accès aux agences, le fichier CA et les libellés APE seront disponibles après le premier enregistrement de l'accès logiciel."
      style={{ marginTop: 8 }}
    />
  ) : (
    <CnOrgaDataPage orgaId={orgaId} initialTab={donneesSubTab} embedded />
  )

  return (
    <div style={{ padding: '16px 0' }}>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        {isNew ? 'Création accès logiciel S&C / MsPro' : 'Modification accès logiciel S&C / MsPro'}
      </Typography.Title>

      <Tabs
        activeKey={mainTab}
        onChange={handleMainTabChange}
        items={[
          {
            key: 'configuration',
            label: 'Configuration',
            children: configurationContent,
          },
          {
            key: 'donnees',
            label: 'Données',
            children: donneesContent,
          },
        ]}
      />
    </div>
  )
}

function SectionCard({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <Card id={id} title={title} size="small" style={{ scrollMarginTop: 64 }}>
      {children}
    </Card>
  )
}
